import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
    Box,
    Button,
    Container,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
    Snackbar,
    Alert,
    Divider,
    Card,
    CardContent
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { countries, indianStates } from '../../Constants/constants';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    marginBottom: theme.spacing(3),
}));

function OfflineRegistration() {
    const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm();
    const [marathonName, setMarathonName] = useState('');
    const [category, setCategory] = useState('');
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [totalPrice, setTotalPrice] = useState(0);
    const [tshirtSizes, setTshirtSizes] = useState([]);
    const [additionalTshirtQuantity, setAdditionalTshirtQuantity] = useState(0);

    // Constants for pricing — add new events here when needed
    const PRICE_MAP = {
        'RFH She run 2025': 800,
        'RFH Juniors run 2025': 599,
        'RFH Juniors Run 2026': 699,
        'RFH Akshara Run 2026': 849,
    };
    const ADDITIONAL_TSHIRT_PRICE_MAP = {
        'RFH She run 2025': 225,
        'RFH Juniors run 2025': 225,
        'RFH Juniors Run 2026': 250,
        'RFH Akshara Run 2026': 250,
    };
    const BREAKFAST_PRICE_MAP = {
        'RFH She run 2025': 80,
        'RFH Juniors run 2025': 80,
        'RFH Juniors Run 2026': 100,
        'RFH Akshara Run 2026': 100,
    };
    const ADDITIONAL_TSHIRT_PRICE = ADDITIONAL_TSHIRT_PRICE_MAP[marathonName] || 225;
    const BREAKFAST_PRICE = BREAKFAST_PRICE_MAP[marathonName] || 80;

    // T-shirt size options based on marathon
    const KIDS_TSHIRT_SIZES = [
        { value: '24', label: 'Size 24' },
        { value: '26', label: 'Size 26' },
        { value: '28', label: 'Size 28' },
        { value: '30', label: 'Size 30' },
        { value: '32', label: 'Size 32' },
        { value: '34', label: 'Size 34' },
        { value: '36', label: 'Size 36' },
        { value: '38', label: 'Size 38' },
        { value: '40', label: 'Size 40' },
        { value: '42', label: 'Size 42' },
        { value: '44', label: 'Size 44' },
        { value: '46', label: 'Size 46' }
    ];
    const TSHIRT_SIZE_OPTIONS = {
        'RFH She run 2025': [
            { value: 'XS', label: 'Extra Small (XS)' },
            { value: 'S', label: 'Small (S)' },
            { value: 'M', label: 'Medium (M)' },
            { value: 'L', label: 'Large (L)' },
            { value: 'XL', label: 'Extra Large (XL)' },
            { value: 'XXL', label: 'Double XL (XXL)' }
        ],
        'RFH Juniors run 2025': KIDS_TSHIRT_SIZES,
        'RFH Juniors Run 2026': KIDS_TSHIRT_SIZES,
        'RFH Akshara Run 2026': [
            { value: 'XS', label: 'Extra Small (XS)' },
            { value: 'S', label: 'Small (S)' },
            { value: 'M', label: 'Medium (M)' },
            { value: 'L', label: 'Large (L)' },
            { value: 'XL', label: 'Extra Large (XL)' },
            { value: 'XXL', label: 'Double XL (XXL)' }
        ],
    };

    // Categories based on marathon
    const CATEGORIES = {
        'RFH She run 2025': [
            { value: 'Chennamma-Run', label: 'Chennamma Run' },
            { value: 'Jhansi-Run', label: 'Jhansi Run' }
        ],
        'RFH Juniors run 2025': [
            { value: 'Champs-Run', label: 'Champs Run (Ages 3-6, 800m)' },
            { value: 'Power-Run', label: 'Power Run (Ages 7-13, 1.5K)' },
            { value: 'Bolts-Run', label: 'Bolts Run (Ages 14-18, 2.5K)' }
        ],
        'RFH Juniors Run 2026': [
            { value: 'Champs-Run', label: 'Champs Run (Ages 3-6, 800m)' },
            { value: 'Power-Run', label: 'Power Run (Ages 7-10, 1.1K)' },
            { value: 'Bolts-Run', label: 'Bolts Run (Ages 11-15, 1.6K)' },
            { value: 'Open-Run', label: 'Open Run (Ages 16+, 1.6K)' }
        ],
        'RFH Akshara Run 2026': [
            { value: 'Fun-Run', label: 'Fun Run (2.5 kms)' },
            { value: 'Challenge-Run', label: 'Challenge Run (5 kms)' }
        ]
    };

    // Watch for changes to calculate total price
    const watchAdditionalTshirt = watch('additionalTshirt', 'No');
    const watchAdditionalTshirtQuantity = watch('additionalTshirtQuantity', '0');
    const watchAdditionalBreakfast = watch('additionalBreakfast', '0');
    const watchDonation = watch('donation', '');

    useEffect(() => {
        if (marathonName) {
            calculateTotalPrice();
        }
    }, [marathonName, category, watchAdditionalTshirt, watchAdditionalTshirtQuantity, watchAdditionalBreakfast, watchDonation]);

    const handleMarathonChange = (event) => {
        console.log("event",event)
        const selectedMarathon = event.target.value;
        console.log("Selected marathon:", selectedMarathon);
        setMarathonName(selectedMarathon);
        setCategory('');
        setValue('marathonName', selectedMarathon);
        setValue('category', '');
        setValue('TshirtSize', '');
        
        // Reset additional t-shirt sizes when marathon changes
        setTshirtSizes([]);
        setAdditionalTshirtQuantity(0);
        setValue('additionalTshirtQuantity', '0');
    };

    const handleCategoryChange = (event) => {
        setCategory(event.target.value);
        setValue('category', event.target.value);
    };

    const handleTshirtQuantityChange = (e) => {
        const value = e.target.value;
        const quantity = parseInt(value) || 0;
        setAdditionalTshirtQuantity(quantity);
        
        // Update tshirt sizes array
        const newSizes = [...tshirtSizes];
        if (quantity > newSizes.length) {
            // Add new empty sizes
            for (let i = newSizes.length; i < quantity; i++) {
                newSizes.push('');
            }
        } else if (quantity < newSizes.length) {
            // Remove extra sizes
            newSizes.splice(quantity);
        }
        setTshirtSizes(newSizes);
        register('additionalTshirtQuantity').onChange(e);
    };

    const handleSizeChange = (index, size) => {
        const newSizes = [...tshirtSizes];
        newSizes[index] = size;
        setTshirtSizes(newSizes);
    };

    const calculateTotalPrice = () => {
        if (!marathonName) return;

        let price = PRICE_MAP[marathonName] || 0;
        const tshirtPrice = ADDITIONAL_TSHIRT_PRICE_MAP[marathonName] || 225;
        const breakfastPrice = BREAKFAST_PRICE_MAP[marathonName] || 80;

        if (watchAdditionalTshirt === 'Yes') {
            const qty = parseInt(watchAdditionalTshirtQuantity) || 0;
            price += qty * tshirtPrice;
        }

        const additionalBreakfast = parseInt(watchAdditionalBreakfast) || 0;
        price += additionalBreakfast * breakfastPrice;

        const donation = parseInt(watchDonation) || 0;
        price += donation;

        setTotalPrice(price);
    };

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const onSubmit = async (data) => {
        setLoading(true);
        
        try {
            // Add additional fields
            const formData = {
                ...data,
                totalPrice,
                additionalTshirtSize: tshirtSizes.join(','),
                date: new Date().toISOString(),
            };

            // Create a payment object similar to what would be created by Razorpay/PhonePe
            const paymentDetails = {
                success: true,
                code: 'PAYMENT_SUCCESS',
                amount: totalPrice * 100, // In paise/cents
                // amount: 100, // for testing
                paymentGateway: 'OFFLINE', // This is the key field to identify offline payments
                paymentMethod: data.paymentMethodType || 'cash',
                receiptNumber: data.receiptNumber || '',
                notes: data.paymentNotes || ''
            };

            // Prepare the final data object
            const finalData = {
                marathonName: marathonName,
                userDetails: formData,
                paymentDetails: paymentDetails,
                isOfflineRegistration: true
            };

            // Send to server
            const response = await fetch(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/marathons/add-offline-registration`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(finalData),
            });

            if (!response.ok) {
                throw new Error('Failed to add offline registration');
            }

            const result = await response.json();
            
            setSnackbar({
                open: true,
                message: 'Offline registration added successfully!',
                severity: 'success'
            });

            // Reset form after successful submission
            reset({
                marathonName: '',
                category: '',
                TshirtSize: '',
                additionalTshirt: 'No',
                additionalTshirtQuantity: '0',
                additionalBreakfast: '0',
                donation: '',
                fullName: '',
                email: '',
                mobNo: '',
                dob: '',
                gender: '',
                bloodGroup: '',
                address: '',
                city: '',
                state: '',
                country: '',
                nationality: '',
                emergencyName: '',
                emergencyNo: '',
                paymentMethodType: '',
                receiptNumber: '',
                paymentNotes: ''
            });

            // Reset local states
            setMarathonName('');
            setCategory('');
            setTshirtSizes([]);
            setAdditionalTshirtQuantity(0);
            setTotalPrice(0);

            // Reset form values using setValue
            setValue('additionalTshirt', 'No');
            setValue('additionalTshirtQuantity', '0');
            setValue('additionalBreakfast', '0');
            setValue('donation', '');

        } catch (error) {
            console.error('Error adding offline registration:', error);
            setSnackbar({
                open: true,
                message: `Error: ${error.message}`,
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
                <Typography variant="h4" gutterBottom>
                    Add Offline Registration
                </Typography>
                <Divider sx={{ mb: 4 }} />
                
                <form onSubmit={handleSubmit(onSubmit)}>
                    <StyledPaper>
                        <Typography variant="h5" gutterBottom>
                            Event Details
                        </Typography>
                        
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required error={!!errors.marathonName}>
                                    <InputLabel>Marathon</InputLabel>
                                    <Select
                                        value={watch('marathonName', '')}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setMarathonName(value);
                                            setValue('marathonName', value);
                                        }}
                                        label="Marathon"
                                    >
                                        <MenuItem value="RFH She run 2025">RFH She run 2025</MenuItem>
                                        <MenuItem value="RFH Juniors run 2025">RFH Juniors run 2025</MenuItem>
                                        <MenuItem value="RFH Juniors Run 2026">RFH Juniors Run 2026</MenuItem>
                                        <MenuItem value="RFH Akshara Run 2026">RFH Akshara Run 2026</MenuItem>
                                    </Select>
                                    {errors.marathonName && <FormHelperText>{errors.marathonName.message}</FormHelperText>}
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required disabled={!marathonName} error={!!errors.category}>
                                    <InputLabel>Category</InputLabel>
                                    <Select
                                        value={watch('category', '')}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setCategory(value);
                                            setValue('category', value);
                                        }}
                                        label="Category"
                                    >
                                        {marathonName && CATEGORIES[marathonName]?.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.category && <FormHelperText>{errors.category.message}</FormHelperText>}
                                </FormControl>
                            </Grid>
                        </Grid>
                    </StyledPaper>
                    
                    <StyledPaper>
                        <Typography variant="h5" gutterBottom>
                            Personal Information
                        </Typography>
                        
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Full Name"
                                    {...register('fullName', { required: 'Full name is required' })}
                                    error={!!errors.fullName}
                                    helperText={errors.fullName?.message}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    type="email"
                                    {...register('email', { 
                                        required: 'Email is required',
                                        pattern: {
                                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                            message: 'Invalid email address'
                                        }
                                    })}
                                    error={!!errors.email}
                                    helperText={errors.email?.message}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Mobile Number"
                                    {...register('mobNo', { 
                                        required: 'Mobile number is required',
                                        pattern: {
                                            value: /^[0-9]{10}$/,
                                            message: 'Mobile number must be 10 digits'
                                        }
                                    })}
                                    error={!!errors.mobNo}
                                    helperText={errors.mobNo?.message}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Date of Birth"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    {...register('dob', { required: 'Date of birth is required' })}
                                    error={!!errors.dob}
                                    helperText={errors.dob?.message}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required error={!!errors.gender}>
                                    <InputLabel>Gender</InputLabel>
                                    <Select
                                        value={watch('gender', '')}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setValue('gender', value);
                                        }}
                                        label="Gender"
                                    >
                                        <MenuItem value="male">Male</MenuItem>
                                        <MenuItem value="female">Female</MenuItem>
                                        <MenuItem value="other">Other</MenuItem>
                                    </Select>
                                    {errors.gender && <FormHelperText>{errors.gender.message}</FormHelperText>}
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required error={!!errors.bloodGroup}>
                                    <InputLabel>Blood Group</InputLabel>
                                    <Select
                                        value={watch('bloodGroup', '')}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setValue('bloodGroup', value);
                                        }}
                                        label="Blood Group"
                                    >
                                        <MenuItem value="A+">A+</MenuItem>
                                        <MenuItem value="A-">A-</MenuItem>
                                        <MenuItem value="B+">B+</MenuItem>
                                        <MenuItem value="B-">B-</MenuItem>
                                        <MenuItem value="AB+">AB+</MenuItem>
                                        <MenuItem value="AB-">AB-</MenuItem>
                                        <MenuItem value="O+">O+</MenuItem>
                                        <MenuItem value="O-">O-</MenuItem>
                                    </Select>
                                    {errors.bloodGroup && <FormHelperText>{errors.bloodGroup.message}</FormHelperText>}
                                </FormControl>
                            </Grid>
                        </Grid>
                    </StyledPaper>
                    
                    <StyledPaper>
                        <Typography variant="h5" gutterBottom>
                            Address
                        </Typography>
                        
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Address"
                                    multiline
                                    rows={2}
                                    {...register('address', { required: 'Address is required' })}
                                    error={!!errors.address}
                                    helperText={errors.address?.message}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="City"
                                    {...register('city', { required: 'City is required' })}
                                    error={!!errors.city}
                                    helperText={errors.city?.message}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required error={!!errors.state}>
                                    <InputLabel>State</InputLabel>
                                    <Select
                                        value={watch('state', '')}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setValue('state', value);
                                        }}
                                        label="State"
                                    >
                                        {indianStates.map((state) => (
                                            <MenuItem key={state.code} value={state.code}>
                                                {state.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.state && <FormHelperText>{errors.state.message}</FormHelperText>}
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required error={!!errors.country}>
                                    <InputLabel>Country</InputLabel>
                                    <Select
                                        value={watch('country', '')}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setValue('country', value);
                                        }}
                                        label="Country"
                                    >
                                        {countries.map((country) => (
                                            <MenuItem key={country.code} value={country.code}>
                                                {country.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.country && <FormHelperText>{errors.country.message}</FormHelperText>}
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Nationality"
                                    defaultValue="Indian"
                                    {...register('nationality', { required: 'Nationality is required' })}
                                    error={!!errors.nationality}
                                    helperText={errors.nationality?.message}
                                />
                            </Grid>
                        </Grid>
                    </StyledPaper>
                    
                    <StyledPaper>
                        <Typography variant="h5" gutterBottom>
                            T-Shirt & Add-ons
                        </Typography>
                        
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required disabled={!marathonName} error={!!errors.TshirtSize}>
                                    <InputLabel>T-Shirt Size</InputLabel>
                                    <Select
                                        value={watch('TshirtSize', '')}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setValue('TshirtSize', value);
                                        }}
                                        label="T-Shirt Size"
                                    >
                                        {marathonName && TSHIRT_SIZE_OPTIONS[marathonName]?.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.TshirtSize && <FormHelperText>{errors.TshirtSize.message}</FormHelperText>}
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Additional T-Shirt</InputLabel>
                                    <Select
                                        value={watch('additionalTshirt', '')}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setValue('additionalTshirt', value);
                                        }}
                                        label="Additional T-Shirt"
                                    >
                                        <MenuItem value="Yes">Yes</MenuItem>
                                        <MenuItem value="No">No</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            
                            {watchAdditionalTshirt === 'Yes' && (
                                <>
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Additional T-Shirt Quantity</InputLabel>
                                            <Select
                                                value={watch('additionalTshirtQuantity', '0')}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setAdditionalTshirtQuantity(parseInt(value));
                                                    setValue('additionalTshirtQuantity', value);
                                                }}
                                            >
                                                {[0, 1, 2, 3, 4, 5].map((num) => (
                                                    <MenuItem key={num} value={num.toString()}>
                                                        {num}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    
                                    {additionalTshirtQuantity > 0 && (
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1" gutterBottom>
                                                Additional T-Shirt Sizes
                                            </Typography>
                                            <Grid container spacing={2}>
                                                {Array.from({ length: additionalTshirtQuantity }).map((_, index) => (
                                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>Size {index + 1}</InputLabel>
                                                            <Select
                                                                value={tshirtSizes[index] || ''}
                                                                onChange={(e) => handleSizeChange(index, e.target.value)}
                                                            >
                                                                {marathonName && TSHIRT_SIZE_OPTIONS[marathonName]?.map((option) => (
                                                                    <MenuItem key={option.value} value={option.value}>
                                                                        {option.label}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Grid>
                                    )}
                                </>
                            )}
                            
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Additional Breakfast</InputLabel>
                                    <Select
                                        value={watch('additionalBreakfast', '0')}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setValue('additionalBreakfast', value);
                                        }}
                                        label="Additional Breakfast"
                                    >
                                        {[0, 1, 2, 3, 4, 5].map((num) => (
                                            <MenuItem key={num} value={num.toString()}>
                                                {num}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>₹{BREAKFAST_PRICE_MAP[marathonName] || 80} per additional breakfast</FormHelperText>
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Donation (Optional)"
                                    type="number"
                                    InputProps={{ inputProps: { min: 0 } }}
                                    {...register('donation')}
                                />
                            </Grid>
                        </Grid>
                    </StyledPaper>
                    
                    <StyledPaper>
                        <Typography variant="h5" gutterBottom>
                            Emergency Contact
                        </Typography>
                        
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Emergency Contact Name"
                                    {...register('emergencyName', { required: 'Emergency contact name is required' })}
                                    error={!!errors.emergencyName}
                                    helperText={errors.emergencyName?.message}
                                />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Emergency Contact Number"
                                    {...register('emergencyNo', { 
                                        required: 'Emergency contact number is required',
                                        pattern: {
                                            value: /^[0-9]{10}$/,
                                            message: 'Emergency contact number must be 10 digits'
                                        }
                                    })}
                                    error={!!errors.emergencyNo}
                                    helperText={errors.emergencyNo?.message}
                                />
                            </Grid>
                        </Grid>
                    </StyledPaper>
                    
                    <StyledPaper>
                        <Typography variant="h5" gutterBottom>
                            Payment Information
                        </Typography>
                        
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required error={!!errors.paymentMethodType}>
                                    <InputLabel>Payment Method</InputLabel>
                                    <Select
                                        value={watch('paymentMethodType', '')}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setValue('paymentMethodType', value);
                                        }}
                                        label="Payment Method"
                                    >
                                        <MenuItem value="cash">Cash</MenuItem>
                                        <MenuItem value="upi">UPI</MenuItem>
                                        <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                                        <MenuItem value="cheque">Cheque</MenuItem>
                                        <MenuItem value="other">Other</MenuItem>
                                    </Select>
                                    {errors.paymentMethodType && <FormHelperText>{errors.paymentMethodType.message}</FormHelperText>}
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Receipt/Reference Number"
                                    {...register('receiptNumber')}
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Payment Notes"
                                    multiline
                                    rows={2}
                                    {...register('paymentNotes')}
                                />
                            </Grid>
                        </Grid>
                    </StyledPaper>
                    
                    <Card sx={{ mb: 4 }}>
                        <CardContent>
                            <Typography variant="h5" gutterBottom>
                                Order Summary
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            
                            <Grid container spacing={2}>
                                <Grid item xs={8}>
                                    <Typography>Registration Fee:</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography align="right">₹{marathonName ? PRICE_MAP[marathonName] : 0}</Typography>
                                </Grid>
                                
                                {watchAdditionalTshirt === 'Yes' && parseInt(watchAdditionalTshirtQuantity) > 0 && (
                                    <>
                                        <Grid item xs={8}>
                                            <Typography>Additional T-shirts ({watchAdditionalTshirtQuantity} × ₹{ADDITIONAL_TSHIRT_PRICE}):</Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography align="right">₹{parseInt(watchAdditionalTshirtQuantity) * ADDITIONAL_TSHIRT_PRICE}</Typography>
                                        </Grid>
                                    </>
                                )}
                                
                                {parseInt(watchAdditionalBreakfast) > 0 && (
                                    <>
                                        <Grid item xs={8}>
                                            <Typography>Additional Breakfast ({watchAdditionalBreakfast} × ₹{BREAKFAST_PRICE}):</Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography align="right">₹{parseInt(watchAdditionalBreakfast) * BREAKFAST_PRICE}</Typography>
                                        </Grid>
                                    </>
                                )}
                                
                                {watchDonation && parseInt(watchDonation) > 0 && (
                                    <>
                                        <Grid item xs={8}>
                                            <Typography>Donation:</Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography align="right">₹{watchDonation}</Typography>
                                        </Grid>
                                    </>
                                )}
                                
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                </Grid>
                                
                                <Grid item xs={8}>
                                    <Typography variant="h6">Total:</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="h6" align="right">₹{totalPrice}</Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={loading}
                        >
                            {loading ? 'Submitting...' : 'Add Registration'}
                        </Button>
                    </Box>
                </form>
                
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    );
}

export default OfflineRegistration;
